import { useEffect, useState, useMemo } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Star, Trash2, Eye, EyeOff, MessageSquare, ThumbsUp, ThumbsDown, CheckCircle2, Sparkles } from "lucide-react";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

export default function AdminReviews() {
  const [reviews, setReviews] = useState([]);
  const [filter, setFilter] = useState("all");
  const [loading, setLoading] = useState(true);

  async function load() {
    setLoading(true);
    try {
      const { data } = await api.get(`/admin/reviews${filter !== "all" ? `?status=${filter}` : ""}`);
      setReviews(data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally { setLoading(false); }
  }
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filter]);

  async function moderate(id, patch) {
    try {
      await api.patch(`/admin/reviews/${id}`, patch);
      toast.success("Updated");
      load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  }

  async function remove(id) {
    if (!window.confirm("Delete this review permanently?")) return;
    try {
      await api.delete(`/admin/reviews/${id}`);
      toast.success("Deleted");
      load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  }

  const stats = useMemo(() => {
    const submitted = reviews.filter((r) => (r.rating || 0) > 0);
    const total = submitted.length;
    const published = submitted.filter((r) => r.status === "published").length;
    const pending = submitted.filter((r) => r.status === "pending_review").length;
    const avg = total > 0 ? (submitted.reduce((s, r) => s + r.rating, 0) / total).toFixed(2) : "—";
    const wouldRec = submitted.filter((r) => r.would_recommend !== null);
    const recPct = wouldRec.length > 0 ? Math.round((wouldRec.filter((r) => r.would_recommend).length / wouldRec.length) * 100) : null;
    return { total, published, pending, avg, recPct };
  }, [reviews]);

  return (
    <div className="space-y-8" data-testid="admin-reviews-page">
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4">
        <div>
          <div className="eyebrow mb-2">Client feedback</div>
          <h1 className="font-serif text-4xl font-light">Reviews</h1>
          <p className="text-sm text-muted-foreground mt-2 max-w-xl">
            Moderate client feedback. Approved reviews appear on your homepage in a carousel and grid.
          </p>
        </div>
        <Select value={filter} onValueChange={setFilter}>
          <SelectTrigger className="w-[200px]" data-testid="reviews-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All</SelectItem>
            <SelectItem value="pending_invite">Awaiting submission</SelectItem>
            <SelectItem value="pending_review">Pending review</SelectItem>
            <SelectItem value="published">Published</SelectItem>
            <SelectItem value="hidden">Hidden</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3" data-testid="reviews-stats">
        <Stat label="Submitted" value={stats.total} />
        <Stat label="Average rating" value={stats.avg} stars />
        <Stat label="Would recommend" value={stats.recPct !== null ? `${stats.recPct}%` : "—"} />
        <Stat label="Pending approval" value={stats.pending} />
      </div>

      {loading && <div className="text-muted-foreground">Loading…</div>}
      {!loading && reviews.length === 0 && (
        <div className="border border-dashed border-border p-16 text-center text-muted-foreground" data-testid="reviews-empty">
          <Star className="w-8 h-8 mx-auto text-muted-foreground/40 mb-3" />
          <div className="font-serif text-2xl text-foreground/60">No reviews yet</div>
          <div className="text-sm mt-1">When you mark a gallery as delivered, your client gets an auto-feedback request.</div>
        </div>
      )}

      <div className="grid lg:grid-cols-2 gap-4">
        {reviews.map((r) => <ReviewCard key={r.id} r={r} onModerate={moderate} onDelete={remove} />)}
      </div>
    </div>
  );
}

function Stat({ label, value, stars }) {
  return (
    <div className="bg-card border border-border p-5">
      <div className="eyebrow">{label}</div>
      <div className="font-serif text-3xl mt-2 flex items-center gap-2">
        {value}
        {stars && value !== "—" && <Star className="w-5 h-5 fill-amber-400 text-amber-400" />}
      </div>
    </div>
  );
}

function ReviewCard({ r, onModerate, onDelete }) {
  const submitted = (r.rating || 0) > 0;
  const [response, setResponse] = useState(r.admin_response || "");

  return (
    <div className="bg-card border border-border p-5" data-testid={`review-card-${r.id}`}>
      <div className="flex items-start justify-between gap-3 mb-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <div className="font-medium truncate">{r.customer_name || "Client"}</div>
            <StatusPill status={r.status} />
            {r.is_featured && <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] uppercase tracking-[0.15em] bg-amber-50 text-amber-800 border border-amber-300"><Sparkles className="w-3 h-3" /> Featured</span>}
          </div>
          <div className="text-xs text-muted-foreground mt-1">
            {r.service_name} {r.booking_reference && <>· {r.booking_reference}</>}
          </div>
        </div>
        <button onClick={() => onDelete(r.id)} className="text-muted-foreground hover:text-destructive" aria-label="Delete" data-testid={`delete-review-${r.id}`}>
          <Trash2 className="w-4 h-4" />
        </button>
      </div>

      {submitted ? (
        <>
          <div className="flex items-center gap-3 mb-3">
            <div className="inline-flex" data-testid={`rating-${r.id}`}>
              {[1, 2, 3, 4, 5].map((n) => (
                <Star key={n} className={`w-5 h-5 ${n <= r.rating ? "fill-amber-400 text-amber-400" : "text-stone-300"}`} />
              ))}
            </div>
            {r.would_recommend === true && <span className="inline-flex items-center gap-1 text-xs text-emerald-700"><ThumbsUp className="w-3 h-3" /> Would recommend</span>}
            {r.would_recommend === false && <span className="inline-flex items-center gap-1 text-xs text-rose-700"><ThumbsDown className="w-3 h-3" /> Wouldn't recommend</span>}
            {r.allow_public === false && <span className="text-xs text-muted-foreground">private</span>}
          </div>
          {r.comment && (
            <blockquote className="border-l-2 border-foreground/40 pl-3 italic text-sm text-muted-foreground mb-3">"{r.comment}"</blockquote>
          )}
          {r.photo_file_id && (
            <img src={`${BACKEND}/api/files/${r.photo_file_id}?w=400`} alt="" className="w-32 h-32 object-cover border border-border mb-3" />
          )}
          <div className="flex flex-wrap gap-2">
            {r.status !== "published" && (
              <button onClick={() => onModerate(r.id, { status: "published" })} className="inline-flex items-center gap-1.5 px-3 h-8 text-xs uppercase tracking-[0.15em] bg-emerald-700 hover:bg-emerald-800 text-white" data-testid={`publish-${r.id}`}>
                <Eye className="w-3.5 h-3.5" /> Publish
              </button>
            )}
            {r.status === "published" && (
              <>
                <button onClick={() => onModerate(r.id, { status: "hidden" })} className="inline-flex items-center gap-1.5 px-3 h-8 text-xs uppercase tracking-[0.15em] border border-border hover:border-foreground/60" data-testid={`hide-${r.id}`}>
                  <EyeOff className="w-3.5 h-3.5" /> Hide
                </button>
                <button onClick={() => onModerate(r.id, { is_featured: !r.is_featured })} className={`inline-flex items-center gap-1.5 px-3 h-8 text-xs uppercase tracking-[0.15em] ${r.is_featured ? "bg-amber-100 text-amber-900 border border-amber-300" : "border border-border hover:border-foreground/60"}`} data-testid={`feature-${r.id}`}>
                  <Sparkles className="w-3.5 h-3.5" /> {r.is_featured ? "Unfeature" : "Feature"}
                </button>
              </>
            )}
            {r.status === "hidden" && (
              <button onClick={() => onModerate(r.id, { status: "published" })} className="inline-flex items-center gap-1.5 px-3 h-8 text-xs uppercase tracking-[0.15em] bg-emerald-700 hover:bg-emerald-800 text-white" data-testid={`unhide-${r.id}`}>
                <Eye className="w-3.5 h-3.5" /> Republish
              </button>
            )}
          </div>
          <details className="mt-3 text-sm">
            <summary className="cursor-pointer text-xs uppercase tracking-[0.15em] text-muted-foreground hover:text-foreground inline-flex items-center gap-1.5"><MessageSquare className="w-3.5 h-3.5" /> Public response (optional)</summary>
            <textarea
              rows={2}
              value={response}
              onChange={(e) => setResponse(e.target.value)}
              className="mt-2 w-full bg-transparent border border-border p-2 outline-none focus:border-foreground text-sm"
              placeholder="Thank you so much, Sade — it was a pleasure!"
              data-testid={`response-textarea-${r.id}`}
            />
            <button onClick={() => onModerate(r.id, { admin_response: response.trim() })} className="mt-2 px-3 h-8 text-xs uppercase tracking-[0.15em] border border-border hover:border-foreground/60" data-testid={`save-response-${r.id}`}>Save response</button>
          </details>
        </>
      ) : (
        <div className="text-sm text-muted-foreground italic flex items-center gap-2">
          <CheckCircle2 className="w-4 h-4 text-stone-400" />
          Awaiting client submission · invite sent {r.invite_sent_at ? new Date(r.invite_sent_at).toLocaleDateString() : "—"}
        </div>
      )}
    </div>
  );
}

function StatusPill({ status }) {
  const styles = {
    pending_invite: { label: "INVITED", cls: "bg-stone-100 text-stone-600 border-stone-300" },
    pending_review: { label: "PENDING", cls: "bg-amber-50 text-amber-800 border-amber-300" },
    published: { label: "PUBLISHED", cls: "bg-emerald-50 text-emerald-800 border-emerald-300" },
    hidden: { label: "HIDDEN", cls: "bg-stone-100 text-stone-500 border-stone-300" },
  };
  const s = styles[status] || styles.pending_invite;
  return <span className={`inline-flex items-center px-2 py-0.5 text-[10px] uppercase tracking-[0.15em] border ${s.cls}`}>{s.label}</span>;
}
